/**
 * MosaicParentLinkModal.js — v6.32.168
 *
 * Lets a teacher mint + send a parent magic-link for a student.
 * Defaults to also emailing the link via SendGrid; teacher can untick
 * to get a copy-paste-only flow.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, Copy, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCALES = [
  { v: 'auto', label: 'Auto-detect from home language' },
  { v: 'en',   label: 'English' },
  { v: 'es',   label: 'Spanish (Español)' },
  { v: 'zh',   label: 'Mandarin (中文)' },
  { v: 'ar',   label: 'Arabic (العربية)' },
  { v: 'ru',   label: 'Russian (Русский)' },
  { v: 'ht',   label: 'Haitian Creole (Kreyòl)' },
];

const MosaicParentLinkModal = ({ open, onOpenChange, student }) => {
  const [parentEmail, setParentEmail] = useState('');
  const [parentName, setParentName] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [locale, setLocale] = useState('auto');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setParentEmail(''); setParentName(''); setSendEmail(true); setLocale('auto');
    setResult(null); setCopied(false);
  };

  const issue = async () => {
    if (!student?.user_id) { toast.error('No student selected'); return; }
    if (!parentEmail) { toast.error('Parent email required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mosaic/parent-link/${student.user_id}`, {
        parent_email: parentEmail,
        parent_name: parentName || null,
        send_email: sendEmail,
        locale: locale === 'auto' ? null : locale,
      });
      setResult(r.data);
      if (sendEmail && r.data?.email_sent) toast.success(`Link emailed to ${parentEmail} (${(r.data.locale || 'en').toUpperCase()})`);
      else if (sendEmail) toast.warning('Link issued, but email delivery did not confirm');
      else toast.success('Link issued — copy + share manually');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to issue parent link');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.link) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Clipboard write blocked — long-press the link to copy');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="mosaic-parent-link-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Mail className="w-5 h-5 text-violet-600" />
            Send Growth Update to Family
          </DialogTitle>
          <DialogDescription>
            Mint a read-only magic link for{' '}
            <strong>{student?.name || 'this student'}</strong>'s parent or guardian.
            The link is valid for 30 days and requires no sign-in.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="parent-email">Parent / Guardian Email</Label>
              <Input
                id="parent-email"
                data-testid="parent-link-email"
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="parent@example.com"
              />
            </div>
            <div>
              <Label htmlFor="parent-name">Parent / Guardian Name (optional)</Label>
              <Input
                id="parent-name"
                data-testid="parent-link-name"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="Maria's Family"
              />
            </div>
            <div>
              <Label htmlFor="parent-locale">Letter Language</Label>
              <select
                id="parent-locale"
                data-testid="parent-link-locale"
                className="w-full border border-gray-300 rounded h-10 px-2 text-sm"
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
              >
                {LOCALES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              <div className="text-[11px] text-gray-500 mt-1">
                Powered by MOSAIC content engine. "Auto-detect" reads the student's intake home language.
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                data-testid="parent-link-send-email"
              />
              <span>Email the link directly to the parent (SendGrid)</span>
            </label>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="parent-link-cancel">Cancel</Button>
              <Button
                onClick={issue}
                disabled={busy || !parentEmail}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="parent-link-issue"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Issue + Send
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="parent-link-result">
            <div className={`p-3 border-2 ${result.email_sent ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
              <div className="flex items-center gap-2 font-semibold text-sm">
                <CheckCircle2 className={`w-4 h-4 ${result.email_sent ? 'text-emerald-700' : 'text-amber-700'}`} />
                {result.email_sent
                  ? `Magic link emailed to ${parentEmail}`
                  : 'Magic link issued (no email sent — copy + share manually)'}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Expires {new Date(result.expires_at).toLocaleDateString()} · {result.ttl_days || 30}-day TTL
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-widest text-gray-500">Parent magic link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={result.link}
                  readOnly
                  data-testid="parent-link-url"
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  onClick={copyLink}
                  data-testid="parent-link-copy"
                >
                  {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
              <Button variant="outline" onClick={reset} data-testid="parent-link-issue-another">Issue another</Button>
              <Button onClick={() => onOpenChange(false)} className="bg-gray-900 text-white hover:bg-gray-800" data-testid="parent-link-done">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MosaicParentLinkModal;
